import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Search, ChevronUp, ChevronDown, ArrowRight } from 'lucide-react';
import { useKeywords } from '@/hooks/useKeywords';
import { usePlan } from '@/hooks/usePlan';
import { PremiumBlur, PremiumTooltip } from '@/components/PremiumGate';
import SEO from '@/components/SEO';

type SortField = 'keyword' | 'searchCount' | 'searchedAt';
type SortOrder = 'asc' | 'desc';

const PERIOD_OPTIONS = [
  { label: '7d', days: 7, premium: true },
  { label: '30d', days: 30, premium: false },
  { label: '90d', days: 90, premium: true },
  { label: 'All', days: 365, premium: true },
];

const ROWS_PER_PAGE = 25;
const FREE_KEYWORD_LIMIT = 10;

const TopKeywords: React.FC = () => {
  const navigate = useNavigate();
  const { isPremium } = usePlan();
  const [days, setDays] = useState(30);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('searchCount');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [page, setPage] = useState(0);

  // Free users: only 30d period and top 10 (enforced server-side; we also cap client-side)
  const requestLimit = isPremium ? 100 : FREE_KEYWORD_LIMIT;
  const { data: keywords = [], isLoading: loading, error } = useKeywords(requestLimit, days);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder(field === 'searchCount' ? 'desc' : 'asc');
    }
    setPage(0);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronDown className="h-3.5 w-3.5 text-gray-300 ml-1 inline" />;
    return sortOrder === 'asc'
      ? <ChevronUp className="h-3.5 w-3.5 text-blue-500 ml-1 inline" />
      : <ChevronDown className="h-3.5 w-3.5 text-blue-500 ml-1 inline" />;
  };

  const filtered = useMemo(() => {
    let list = [...keywords];
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(k => k.keyword.toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      let av: any, bv: any;
      if (sortField === 'keyword') { av = a.keyword; bv = b.keyword; }
      else if (sortField === 'searchCount') { av = a.searchCount; bv = b.searchCount; }
      else { av = new Date(a.searchedAt).getTime(); bv = new Date(b.searchedAt).getTime(); }
      if (av < bv) return sortOrder === 'asc' ? -1 : 1;
      if (av > bv) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [keywords, searchTerm, sortField, sortOrder]);

  const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const paginated = filtered.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);
  const totalSearches = keywords.reduce((s, k) => s + k.searchCount, 0);

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return iso; }
  };

  return (
    <>
      <SEO
        title="All Keywords — Domain Search Trends | DomBase"
        description="Browse all domain keywords searched on DomBase. Discover trending terms, search volumes, and find your perfect domain."
        canonical="/keywords"
        ogType="website"
      />

      {/* Header */}
      <div className="border-b border-gray-100 bg-white px-4 sm:px-6 lg:px-8 py-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <TrendingUp className="h-5 w-5 text-gray-400 flex-shrink-0" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">All Keywords</h1>
              <p className="text-sm text-gray-400 mt-0.5">Domain keywords searched on DomBase</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {!loading && !error && (
              <div className="text-right hidden sm:block">
                <div className="text-xl font-bold text-gray-900 tabular-nums">{totalSearches.toLocaleString()}</div>
                <div className="text-xs text-gray-400">total searches</div>
              </div>
            )}
            {/* Period toggle */}
            <div className="flex bg-gray-100 rounded-md p-0.5 gap-0.5">
              {PERIOD_OPTIONS.map(opt => {
                const locked = opt.premium && !isPremium;
                const btn = (
                  <button
                    key={opt.days}
                    onClick={() => { if (!locked) { setDays(opt.days); setPage(0); } }}
                    className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
                      days === opt.days
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
                return locked
                  ? <PremiumTooltip key={opt.days} label="Available on Premium" highlight="keywords">{btn}</PremiumTooltip>
                  : btn;
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Intro */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2">
        <p className="text-sm text-gray-500 leading-relaxed">
          Discover what domain names people are actively searching for. These keywords represent real founder and investor intent — an ideal signal for naming your next startup or spotting domain investment opportunities before they peak.
        </p>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* Search + summary row */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Filter keywords…"
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setPage(0); }}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          {!loading && (
            <span className="text-xs text-gray-400 flex-shrink-0 tabular-nums">
              {filtered.length} keyword{filtered.length !== 1 ? 's' : ''}
              {filtered.length !== keywords.length && ` of ${keywords.length}`}
            </span>
          )}
        </div>

        {/* Table card */}
        <div className="bg-white border border-gray-200 rounded-md overflow-hidden">

          {/* Loading */}
          {loading && (
            <div>
              {/* Skeleton header */}
              <div className="flex gap-6 px-4 py-3 border-b border-gray-100 bg-gray-50">
                {['w-6', 'flex-1', 'w-24', 'w-32', 'w-20'].map((w, i) => (
                  <div key={i} className={`h-3 ${w} bg-gray-200 rounded animate-pulse`} />
                ))}
              </div>
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="flex items-center gap-6 px-4 py-3.5 border-b border-gray-100 last:border-b-0 animate-pulse">
                  <div className="w-6 h-4 bg-gray-100 rounded flex-shrink-0" />
                  <div className="flex-1 h-4 bg-gray-100 rounded" style={{ maxWidth: `${80 - i * 5}%` }} />
                  <div className="w-16 h-4 bg-gray-100 rounded flex-shrink-0" />
                  <div className="w-24 h-4 bg-gray-100 rounded flex-shrink-0" />
                  <div className="w-16 h-7 bg-gray-100 rounded flex-shrink-0" />
                </div>
              ))}
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="p-10 text-center space-y-4">
              <p className="text-sm text-red-500">Failed to load keywords.</p>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && filtered.length === 0 && (
            <div className="p-12 text-center">
              <TrendingUp className="h-10 w-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm font-semibold text-gray-700 mb-1">
                {searchTerm ? 'No keywords match your search' : 'No keywords yet'}
              </p>
              <p className="text-xs text-gray-400">
                {searchTerm ? 'Try a different filter.' : 'Run a few domain scans and they\'ll appear here automatically.'}
              </p>
            </div>
          )}

          {/* Table */}
          {!loading && !error && paginated.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left w-12">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">#</span>
                    </th>
                    <th
                      className="px-4 py-3 text-left cursor-pointer select-none hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort('keyword')}
                    >
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Keyword <SortIcon field="keyword" />
                      </span>
                    </th>
                    <th
                      className="px-4 py-3 text-right cursor-pointer select-none hover:bg-gray-100 transition-colors w-32"
                      onClick={() => handleSort('searchCount')}
                    >
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Searches <SortIcon field="searchCount" />
                      </span>
                    </th>
                    <th
                      className="px-4 py-3 text-left cursor-pointer select-none hover:bg-gray-100 transition-colors w-40 hidden sm:table-cell"
                      onClick={() => handleSort('searchedAt')}
                    >
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Last Seen <SortIcon field="searchedAt" />
                      </span>
                    </th>
                    <th className="px-4 py-3 w-24" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginated.map((item, i) => {
                    const globalRank = page * ROWS_PER_PAGE + i + 1;
                    return (
                      <tr
                        key={item.keyword}
                        className="hover:bg-blue-50 transition-colors cursor-pointer group"
                        onClick={() => navigate(`/?q=${encodeURIComponent(item.keyword)}`)}
                      >
                        <td className="px-4 py-3 text-sm tabular-nums text-gray-400 font-medium w-12">
                          {globalRank}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">
                            {item.keyword}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right w-32">
                          <span className="text-sm font-semibold text-gray-700 tabular-nums">
                            {item.searchCount.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400 w-40 hidden sm:table-cell">
                          {formatDate(item.searchedAt)}
                        </td>
                        <td className="px-4 py-3 w-24">
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity">
                            Scan <ArrowRight className="h-3 w-3" />
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Premium blur overlay — shown below the table for free users */}
              {!isPremium && !loading && !error && keywords.length >= FREE_KEYWORD_LIMIT && (
                <PremiumBlur
                  label="Unlock all 100 keywords — Go Premium"
                  highlight="keywords"
                  rows={8}
                />
              )}
            </div>
          )}

          {/* Pagination */}
          {!loading && !error && totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
              <span className="text-xs text-gray-500 tabular-nums">
                {page * ROWS_PER_PAGE + 1}–{Math.min((page + 1) * ROWS_PER_PAGE, filtered.length)} of {filtered.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="px-3 text-xs text-gray-500 tabular-nums">
                  {page + 1} / {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TopKeywords;
